import type { Ref } from 'vue'

import {
  PlayerTagDeleteDocument,
  PlayerTagResetAllDocument,
} from '../../lib/handWrittenGraphql.ts'
import type {
  PlayerTagDeleteMutation,
  PlayerTagResetAllMutation,
} from '../../lib/handWrittenGraphql.ts'
import { isSystemTag } from '../../lib/tagIdentity.ts'
import type { PlayerTag } from '../../types/index.ts'

function snowflakeVar(s: string): string {
  return s
}

/**
 * Result-returning mutation runner signature. Mirrors
 * `useMutationRunners.runReporting` so swapping the runner in
 * `gvgPlannerStore.ts` is a one-line wiring concern.
 */
type ReportingRunner = <_T>(
  mutation: unknown,
  variables: Record<string, unknown>,
) => Promise<boolean>

/**
 * Read-only ctx the reset composable needs. The planner subscription applies
 * the authoritative catalog after each committed step, so the reset never
 * starts a `PlayerTags` query to reconcile its own mutations.
 */
export interface TagResetCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly playerTags: Ref<ReadonlyArray<PlayerTag>>
  readonly activeTagFilter: Ref<string | null>
  /**
   * Result-returning mutation runner. The reset must `await` every
   * step so a step-level failure resolves `false` instead of leaving
   * the catalog half-destroyed.
   */
  readonly runReporting: ReportingRunner
}

export interface TagResetApi {
  /**
   * Bulk-detach every tag assignment for every player in the active guild.
   * Returns `Promise<boolean>` for the mutation sequence; committed state is
   * replaced asynchronously by the planner subscription.
   *
   * Multi-step orchestration:
   *   1. Fire the `playerTagResetAll` mutation (clears assignments).
   *   2. Delete only custom catalog tags. System tags retain their seed keys
   *      so the tactical map and timed-boss objectives continue to resolve.
   *
   * The calling component owns the destructive-action prompt
   * (an in-app dialog) per AGENTS.md's component-locality rule; the
   * store is confirm-agnostic.
   */
  readonly resetAllTags: () => Promise<boolean>
}

/**
 * Owns `resetAllTags` — the only multi-step orchestration in the tag
 * surface. Extracted from `actions.ts` so catalog CRUD remains one mutation
 * per action while reset keeps its bounded clear-and-custom-cleanup flow.
 */
export function useTagResetActions(ctx: TagResetCtx): TagResetApi {
  async function deleteTagForReset(tagId: string): Promise<boolean> {
    try {
      const res = await ctx.runReporting<PlayerTagDeleteMutation>(PlayerTagDeleteDocument, {
        guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
        tagId: Number(tagId),
      })
      if (res === true) return true
      ctx.error.value = 'Could not delete a tag while resetting the catalog.'
      return false
    } catch (e: unknown) {
      ctx.error.value = e instanceof Error ? e.message : String(e)
      return false
    }
  }

  async function resetAllTags(): Promise<boolean> {
    const reset = await ctx.runReporting<PlayerTagResetAllMutation>(PlayerTagResetAllDocument, {
      guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
    })
    if (!reset) return false
    const tagsToDelete = ctx.playerTags.value.filter((tag) => !isSystemTag(tag))
    for (const tag of tagsToDelete) {
      if (!await deleteTagForReset(tag.id)) {
        return false
      }
    }

    if (tagsToDelete.some((tag) => tag.id === ctx.activeTagFilter.value)) {
      ctx.activeTagFilter.value = null
    }
    return true
  }

  return { resetAllTags }
}
